import React from 'react';
import { osc, wobble, lerp, loop, clamp, easeOut, pulse } from '../../core/AnimationUtils';

/*
 * Concept-acting SCENES — Forces (Anivaze v2)
 * A 5-year-old watches the physics HAPPEN in front of them.
 * Pure function of `t` (seconds). Default t=0 renders cleanly.
 * See design-plan/v2/ANIMATION-PATTERN.md.
 */

/* ─── shared helpers ──────────────────────────────────────────────────────── */

/** Up-arrow chevron centred at (cx, cy). */
const UpArrow = (cx: number, cy: number, color: string, scale = 1) => (
  <g transform={`translate(${cx} ${cy}) scale(${scale})`}>
    <polygon points="0,-10 8,2 3,2 3,10 -3,10 -3,2 -8,2" fill={color} opacity={0.85} />
  </g>
);

/** Down-arrow chevron centred at (cx, cy). */
const DownArrow = (cx: number, cy: number, color: string, scale = 1) => (
  <g transform={`translate(${cx} ${cy}) scale(${scale})`}>
    <polygon points="0,10 8,-2 3,-2 3,-10 -3,-10 -3,-2 -8,-2" fill={color} opacity={0.85} />
  </g>
);

/* ━━━ Float vs Sink: cork bobs up; rock sinks — looping ━━━━━━━━━━━━━━━━━━━━ */
export interface FloatSinkSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  t?: number;
}

export const FloatSinkScene: React.FC<FloatSinkSceneProps> = ({
  opacity = 1,
  accentColor = '#f59e0b',
  t = 0,
  label = 'Light things float, heavy things sink!',
}) => {
  /* ── layout ── */
  const tankX = 120, tankY = 80, tankW = 560, tankH = 310;
  const waterTop = tankY + 60;   // y where water surface sits
  const tankBot = tankY + tankH; // inner floor of tank

  /* ── 6-second loop: drop → settle → pause → drop again ── */
  const cycle = loop(t, 6);  // 0..1 per 6s

  /* CORK (floater): drops from above water, floats up and bobs at surface */
  // phase 0-0.15 → cork is above water (dropping in)
  // phase 0.15-0.40 → cork rises to surface
  // phase 0.40-1.00 → bobs happily at surface
  const corkDropPhase = clamp(cycle / 0.15, 0, 1);         // 0→1 during drop
  const corkRisePhase = clamp((cycle - 0.15) / 0.25, 0, 1);
  const corkDropY   = lerp(tankY - 30, waterTop + 40, easeOut(corkDropPhase));
  const corkRiseY   = lerp(waterTop + 40, waterTop - 10, easeOut(corkRisePhase));
  const corkY       = cycle < 0.15 ? corkDropY : corkRiseY;
  // gentle bob once at surface
  const bobAmt      = cycle > 0.4 ? wobble(t, 1.2, 5) : 0;
  const corkFinalY  = clamp(corkY + bobAmt, tankY - 35, waterTop + 5);
  const corkX       = tankX + tankW * 0.28;

  /* ROCK (sinker): drops in at same moment, sinks to bottom */
  const rockDropPhase = clamp(cycle / 0.15, 0, 1);
  const rockSinkPhase = clamp((cycle - 0.15) / 0.35, 0, 1);
  const rockStartY    = tankY - 30;
  const rockWaterY    = waterTop + 20;    // enters water
  const rockFloorY    = tankBot - 22;
  const rockDropY2    = lerp(rockStartY, rockWaterY, easeOut(rockDropPhase));
  const rockSinkY     = lerp(rockWaterY, rockFloorY, easeOut(rockSinkPhase));
  const rockY         = cycle < 0.15 ? rockDropY2 : rockSinkY;
  const rockX         = tankX + tankW * 0.70;

  /* ── water surface ripple ── */
  const rippleAmp = 4 + osc(t, 1.5, 0, 3);
  const ripplePoints = Array.from({ length: 29 }, (_, i) => {
    const rx = tankX + (i / 28) * tankW;
    const ry = waterTop + Math.sin((i / 28) * Math.PI * 4 + t * 2.5) * rippleAmp;
    return `${rx},${ry}`;
  }).join(' ');

  /* ── bubbles rising from the rock (deterministic by index) ── */
  const bubbles = Array.from({ length: 5 }, (_, i) => {
    const bPhase = loop(t + i * 0.7, 2.2);
    const bx = rockX + (i % 3 - 1) * 10;
    const by = lerp(rockFloorY - 10, waterTop + 10, easeOut(bPhase));
    const br = 3 + i * 0.8;
    const bOpacity = cycle > 0.5 ? clamp(1 - bPhase * 1.4, 0, 0.85) : 0;
    return { bx, by, br, bOpacity };
  });

  /* ── arrow visibility ── */
  const arrowFade = clamp((cycle - 0.35) / 0.15, 0, 1);

  return (
    <g opacity={opacity}>
      {/* sky / background */}
      <rect x={0} y={0} width={800} height={450} fill="#e0f2fe" />

      {/* tank body */}
      <rect x={tankX} y={tankY} width={tankW} height={tankH} rx={12}
        fill="rgba(186,230,253,0.35)" stroke="#7dd3fc" strokeWidth={4} />

      {/* water fill */}
      <clipPath id="fs-tank-clip">
        <rect x={tankX + 2} y={waterTop} width={tankW - 4} height={tankH - 60} />
      </clipPath>
      <rect x={tankX + 2} y={waterTop} width={tankW - 4} height={tankH - 60}
        fill="rgba(56,189,248,0.30)" />

      {/* water surface wave */}
      <polyline points={ripplePoints} fill="none" stroke="#38bdf8" strokeWidth={3}
        strokeLinecap="round" />

      {/* ── ROCK ── */}
      <g>
        {/* shadow on floor when settled */}
        {cycle > 0.5 && (
          <ellipse cx={rockX} cy={rockFloorY + 18} rx={22} ry={5}
            fill="rgba(0,0,0,0.18)" opacity={clamp((cycle - 0.5) / 0.1, 0, 1)} />
        )}
        <polygon
          points={`${rockX - 18},${rockY + 10} ${rockX - 14},${rockY - 12} ${rockX + 2},${rockY - 16} ${rockX + 18},${rockY - 8} ${rockX + 14},${rockY + 10}`}
          fill="#78716c" stroke="#44403c" strokeWidth={2}
        />
        {/* shading ridge */}
        <line x1={rockX - 6} y1={rockY - 10} x2={rockX + 6} y2={rockY - 4}
          stroke="#a8a29e" strokeWidth={2} strokeLinecap="round" />
        {/* down-arrows for sinking */}
        {DownArrow(rockX, rockY - 32, '#dc2626', arrowFade)}
        {DownArrow(rockX + 22, rockY - 28, '#dc2626', arrowFade * 0.7)}
      </g>

      {/* bubbles from rock */}
      {bubbles.map((b, i) => (
        <circle key={i} cx={b.bx} cy={b.by} r={b.br}
          fill="none" stroke="#bae6fd" strokeWidth={1.5} opacity={b.bOpacity} />
      ))}

      {/* ── CORK / DUCK ── */}
      <g>
        {/* cork body (rounded rectangle, warm yellow) */}
        <rect x={corkX - 22} y={corkFinalY - 10} width={44} height={22} rx={11}
          fill={accentColor} stroke="#d97706" strokeWidth={2} />
        {/* duck head */}
        <circle cx={corkX + 18} cy={corkFinalY - 14} r={12}
          fill="#fde68a" stroke="#d97706" strokeWidth={2} />
        {/* duck eye */}
        <circle cx={corkX + 22} cy={corkFinalY - 17} r={2.4} fill="#1c1917" />
        {/* beak */}
        <polygon points={`${corkX + 28},${corkFinalY - 14} ${corkX + 34},${corkFinalY - 12} ${corkX + 28},${corkFinalY - 10}`}
          fill="#fb923c" />
        {/* up-arrows for floating */}
        {UpArrow(corkX, corkFinalY - 38, '#16a34a', arrowFade)}
        {UpArrow(corkX - 22, corkFinalY - 34, '#16a34a', arrowFade * 0.7)}
      </g>

      {/* tank bottom bar */}
      <rect x={tankX} y={tankBot - 2} width={tankW} height={12} rx={6}
        fill="#7dd3fc" opacity={0.5} />

      {/* ── labels ── */}
      <text x={corkX} y={tankY + tankH + 32} textAnchor="middle"
        fill="#15803d" fontSize={14} fontWeight={800} fontFamily="Inter,sans-serif">
        FLOATS!
      </text>
      <text x={rockX} y={tankY + tankH + 32} textAnchor="middle"
        fill="#dc2626" fontSize={14} fontWeight={800} fontFamily="Inter,sans-serif">
        SINKS!
      </text>

      {/* friendly caption */}
      <g transform="translate(400 36)">
        <rect x={-270} y={-22} width={540} height={40} rx={20}
          fill="rgba(255,255,255,0.90)" stroke="#bae6fd" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#0f172a"
          fontSize={16} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};

/* ━━━ Magnet: paperclips fly in; wooden block stays put ━━━━━━━━━━━━━━━━━━━━ */
export interface MagnetSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  t?: number;
}

export const MagnetScene: React.FC<MagnetSceneProps> = ({
  opacity = 1,
  accentColor = '#ef4444',
  t = 0,
  label = 'Magnets pull metal things!',
}) => {
  /* ── layout ── */
  const poleR  = 24;   // pole circle radius

  /* ── horseshoe path geometry ──
     The U-shape: left arm top at (330,120), bends down and round, right arm top at (470,120).
     We'll draw it with a path and place coloured pole caps at the tips.
  */
  const lArmX = 348, rArmX = 452;
  const armTopY = 105, armBotY = 210;

  /* ── 5-second loop ── */
  const cycle = loop(t, 5);   // 0..1 per 5s

  /* PAPERCLIPS: 3 on each side, sliding inward and sticking */
  // Each clip i (0..5): 0-2 aim at left pole, 3-5 at right pole
  const clips = Array.from({ length: 6 }, (_, i) => {
    const isLeft = i < 3;
    const poleX  = isLeft ? lArmX : rArmX;
    const poleY  = armBotY;
    // stagger: each clip starts 0.15 further into the cycle
    const offset  = i * 0.12;
    const clipPhase = clamp((cycle - offset) / 0.55, 0, 1);
    // fan out from left or right edge
    const startX = isLeft ? 60 + (i % 3) * 30  : 740 - (i % 3) * 30;
    const startY = 160 + (i % 3) * 45;
    const cx = lerp(startX, poleX, easeOut(clipPhase));
    const cy = lerp(startY, poleY, easeOut(clipPhase));
    // motion lines fade out once stuck (clipPhase > 0.85)
    const lineOpacity = clamp((clipPhase * (1 - clamp((clipPhase - 0.85) / 0.15, 0, 1))), 0, 0.75);
    return { cx, cy, clipPhase, lineOpacity };
  });

  /* WOODEN BLOCK: stays completely still — shows non-magnetic response */
  const blockX = 400, blockY = 355;

  /* gentle pulse on the magnet poles */
  const pulseScale = 1 + pulse(t, 1.4) * 0.07;

  /* repel flash: briefly show two magnets pushing apart around cycle 0.78 */
  const repelPhase = clamp((cycle - 0.76) / 0.08, 0, 1) * clamp((0.94 - cycle) / 0.08, 0, 1);
  const repelOffset = repelPhase * 28;

  /* field arcs — gentle breathing dashes */
  const fieldOpacity = osc(t, 2.2, 0.18, 0.55);

  return (
    <g opacity={opacity}>
      {/* background */}
      <rect x={0} y={0} width={800} height={450} fill="#fef3c7" />

      {/* ── magnetic field arcs (decorative) ── */}
      {[-1, 1].map((side, si) => (
        <g key={si} opacity={fieldOpacity}>
          {[38, 58, 78].map((r, ri) => (
            <ellipse key={ri}
              cx={side === -1 ? lArmX : rArmX} cy={armBotY}
              rx={r} ry={r * 0.55}
              fill="none" stroke={side === -1 ? '#3b82f6' : '#ef4444'}
              strokeWidth={1.8} strokeDasharray="5 5" opacity={0.6 - ri * 0.12}
            />
          ))}
        </g>
      ))}

      {/* ── horseshoe magnet body ── */}
      {/* U bend arc */}
      <path
        d={`M${lArmX - 22},${armTopY} L${lArmX - 22},${armBotY}
            A 44 44 0 0 0 ${rArmX + 22},${armBotY}
            L${rArmX + 22},${armTopY}`}
        fill="none" stroke="#9ca3af" strokeWidth={38} strokeLinecap="butt"
        transform={`translate(0 ${-repelOffset})`}
      />
      <path
        d={`M${lArmX - 22},${armTopY} L${lArmX - 22},${armBotY}
            A 44 44 0 0 0 ${rArmX + 22},${armBotY}
            L${rArmX + 22},${armTopY}`}
        fill="none" stroke="#e5e7eb" strokeWidth={30} strokeLinecap="butt"
        transform={`translate(0 ${-repelOffset})`}
      />

      {/* left arm — blue (north) */}
      <rect x={lArmX - 22} y={armTopY - 10} width={44} height={24} rx={6}
        fill="#3b82f6" stroke="#1d4ed8" strokeWidth={2}
        transform={`translate(0 ${-repelOffset})`}
      />
      <text x={lArmX} y={armTopY + 7} textAnchor="middle"
        fill="#fff" fontSize={14} fontWeight={900} fontFamily="Inter,sans-serif"
        transform={`translate(0 ${-repelOffset})`}>N</text>

      {/* right arm — red (south) */}
      <rect x={rArmX - 22} y={armTopY - 10} width={44} height={24} rx={6}
        fill="#ef4444" stroke="#991b1b" strokeWidth={2}
        transform={`translate(0 ${-repelOffset})`}
      />
      <text x={rArmX} y={armTopY + 7} textAnchor="middle"
        fill="#fff" fontSize={14} fontWeight={900} fontFamily="Inter,sans-serif"
        transform={`translate(0 ${-repelOffset})`}>S</text>

      {/* pole caps (bottom of arms, where clips stick) — pulse radius */}
      <g transform={`translate(0 ${-repelOffset})`}>
        <circle cx={lArmX} cy={armBotY} r={poleR * pulseScale}
          fill="#3b82f6" stroke="#1d4ed8" strokeWidth={3} />
        <circle cx={rArmX} cy={armBotY} r={poleR * pulseScale}
          fill={accentColor} stroke="#991b1b" strokeWidth={3} />
      </g>

      {/* ── paperclips ── */}
      {clips.map((c, i) => (
        <g key={i} transform={`translate(${c.cx} ${c.cy})`}>
          {/* motion lines trailing behind */}
          {c.lineOpacity > 0.05 && (
            <>
              <line x1={-10} y1={0} x2={-22} y2={0}
                stroke="#6b7280" strokeWidth={2} strokeLinecap="round" opacity={c.lineOpacity} />
              <line x1={-8} y1={-5} x2={-18} y2={-9}
                stroke="#6b7280" strokeWidth={1.5} strokeLinecap="round" opacity={c.lineOpacity * 0.6} />
              <line x1={-8} y1={5} x2={-18} y2={9}
                stroke="#6b7280" strokeWidth={1.5} strokeLinecap="round" opacity={c.lineOpacity * 0.6} />
            </>
          )}
          {/* clip body: two overlapping ovals */}
          <ellipse cx={0} cy={-4} rx={7} ry={4}
            fill="none" stroke="#6b7280" strokeWidth={2.5} />
          <ellipse cx={0} cy={4} rx={7} ry={4}
            fill="none" stroke="#6b7280" strokeWidth={2.5} />
        </g>
      ))}

      {/* ── wooden block (non-magnetic — stays put) ── */}
      <g>
        <rect x={blockX - 34} y={blockY - 20} width={68} height={40} rx={7}
          fill="#a16207" stroke="#713f12" strokeWidth={2.5} />
        {/* wood grain lines */}
        <line x1={blockX - 20} y1={blockY - 20} x2={blockX - 22} y2={blockY + 20}
          stroke="#92400e" strokeWidth={1.5} opacity={0.7} />
        <line x1={blockX + 4} y1={blockY - 20} x2={blockX + 2} y2={blockY + 20}
          stroke="#92400e" strokeWidth={1.5} opacity={0.7} />
        <line x1={blockX + 24} y1={blockY - 20} x2={blockX + 22} y2={blockY + 20}
          stroke="#92400e" strokeWidth={1.5} opacity={0.7} />
        {/* "nope" face */}
        <circle cx={blockX - 10} cy={blockY - 3} r={3} fill="#fbbf24" />
        <circle cx={blockX + 10} cy={blockY - 3} r={3} fill="#fbbf24" />
        <path d={`M${blockX - 10},${blockY + 8} Q${blockX},${blockY + 4} ${blockX + 10},${blockY + 8}`}
          fill="none" stroke="#fbbf24" strokeWidth={2} strokeLinecap="round" />
        {/* label above */}
        <text x={blockX} y={blockY - 28} textAnchor="middle"
          fill="#78350f" fontSize={12} fontWeight={700} fontFamily="Inter,sans-serif">
          Wood — stays!
        </text>
      </g>

      {/* repel flash: a second magnet scoots away */}
      {repelPhase > 0.02 && (
        <g opacity={repelPhase}>
          {/* small copy of magnet flipping away to the right */}
          <rect x={rArmX + 60} y={armTopY + 20 + repelOffset * 2} width={54} height={22} rx={6}
            fill="#ef4444" stroke="#991b1b" strokeWidth={2} />
          <text x={rArmX + 87} y={armTopY + 35 + repelOffset * 2} textAnchor="middle"
            fill="#fff" fontSize={12} fontWeight={900} fontFamily="Inter,sans-serif">S S</text>
          {/* repel arrows */}
          <line x1={rArmX + 54} y1={armBotY - 10 - repelOffset}
            x2={rArmX + 80} y2={armBotY - 10 - repelOffset}
            stroke="#f97316" strokeWidth={3} markerEnd="url(#fs-arrow)" strokeLinecap="round" />
        </g>
      )}

      {/* ── caption ── */}
      <g transform="translate(400 425)">
        <rect x={-270} y={-22} width={540} height={38} rx={19}
          fill="rgba(255,255,255,0.92)" stroke="#fde68a" strokeWidth={2} />
        <text x={0} y={4} textAnchor="middle" fill="#0f172a"
          fontSize={16} fontWeight={700} fontFamily="Inter,sans-serif">{label}</text>
      </g>
    </g>
  );
};
